// Used in side-panel-right.component.html

// Angular
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";

// RXJS/Store
import * as fromCreateStore from "#components/content-library/assign-cart/content-builder-store";
import * as assetsActions from "#components/content-library/assign-cart/content-builder-store/actions/assets.actions";
import * as RouterStore from "#core/router-store";
import * as fromCourseStore from "#core/teacher-classes/store";
import * as fromLoginStore from "#login/store";
import { Actions, ofType } from "@ngrx/effects";
import { Store, select } from "@ngrx/store";
import { Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import * as fromCartStore from "../../assign-cart-store";

// Services/Types/Helpers
import { LocalStorage } from "#core/services";
import { LanguageHelper } from "#helpers/language.helper";
import { EditionId, SubjectId } from "@carnegie-learning/common-types";
import { Asset, AssetAssociation, Bundle, CustomActivity, Perform } from "@carnegie-learning/cure-types";
import { AssetsService } from "@carnegie-learning/data-access-cure";
import { ASSETS_SERVICE } from "@carnegie-learning/data-access-angular-cure";
import { LessonsService } from "@carnegie-learning/data-access-angular-passport";
import { FeathersServicePaginationParameters } from "@carnegie-learning/data-access-core";
import { SelectedAssets } from "cli-types";

@Component({
    selector: "assign-cart-content-builder",
    templateUrl: "./assign-cart-content-builder.component.html",
    styleUrls: ["./assign-cart-content-builder.component.scss"],
    standalone: false
})
export class AssignCartContentBuilderComponent implements OnInit, OnDestroy {
    headerTitle = "";
    helperText = "";

    programsLoaded$?: Observable<boolean>;
    assetInUse$?: Observable<boolean>;
    activePrograms$?: Observable<AssetAssociation[]>;
    activeEditions$?: Observable<AssetAssociation[]>;
    isDistributor = false;
    userGUID$?: Observable<any>;
    languageID?: number;
    subjectId: SubjectId = "";
    editionId: EditionId = "";

    isTeacher = false;
    isMath = false;
    selectedAssets: SelectedAssets = { editionId: "", assets: [] };
    params?: FeathersServicePaginationParameters<Asset>;
    convertToLesson = false;
    lessonFormData?: Bundle; // Data stored in the cart store for data retention between min and expand cart state
    lessonFormValidity = false;
    editingLesson = false;
    customActivityFormData: CustomActivity | null = null;
    performActivityFormData: Perform | null = null;
    activityFormValidity = false;
    editingActivity = false;
    preventDoubleClick = false;
    clonedAsset: Asset | null = null;
    @Input() creatingLesson = false;
    @Input() creatingActivity = false;
    @Input() assignCartOpen = false;
    @Output() sidePanelRightClose = new EventEmitter<void>();

    private ngUnsubscribe = new Subject();

    constructor(
        private cartStore: Store<fromCartStore.CartState>,
        private createStore: Store<fromCreateStore.CreateState>,
        private courseStore: Store<fromCourseStore.CoursesState>,
        private routerStore: Store<RouterStore.RouterStateUrl>,
        private loginStore: Store<fromLoginStore.UserState>,
        private localStorage: LocalStorage,
        private router: Router,
        private route: ActivatedRoute,
        private lessonsService: LessonsService,
        private languageHelper: LanguageHelper,
        private cdr: ChangeDetectorRef,
        @Inject(ASSETS_SERVICE) private assetsService: AssetsService,
        private actions$: Actions
    ) {}

    ngOnInit() {
        this.preventDoubleClick = false;

        this.programsLoaded$ = this.courseStore.pipe(
            select(fromCourseStore.getActiveSubjectsLoaded)
        );

        this.activePrograms$ = this.courseStore.pipe(
            select(fromCourseStore.getAllActiveSubjects)
        );

        this.activeEditions$ = this.courseStore.pipe(
            select(fromCourseStore.getAllActiveEditions)
        );

        this.userGUID$ = this.loginStore.pipe(
            select(fromLoginStore.getUserGUID)
        );
        this.assetInUse$ = this.cartStore.pipe(
            select(fromCartStore.getAssetInUse)
        );

        this.createStore
            .pipe(select(fromCreateStore.getSelectedAssets))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((selectedAssets: SelectedAssets) => {
                this.selectedAssets = selectedAssets;
            });
        this.createStore
            .pipe(select(fromCreateStore.getClonedAsset))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((clonedAsset: Asset | null) => {
                this.clonedAsset = clonedAsset;
            });
        this.routerStore
            .pipe(select(RouterStore.getRouterState))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((router) => {
                if (router) {
                    if (router.state.params.editionId) {
                        this.editionId = router.state.params.editionId;
                    } else {
                        this.cartStore
                            .pipe(select(fromCartStore.getProgramSubjectId))
                            .pipe(takeUntil(this.ngUnsubscribe))
                            .subscribe((subjectId) => {
                                if (subjectId) {
                                    this.subjectId = subjectId;
                                }
                            });
                        this.cartStore
                            .pipe(select(fromCartStore.getProgramEditionId))
                            .pipe(takeUntil(this.ngUnsubscribe))
                            .subscribe((editionId) => {
                                if (editionId) {
                                    this.editionId = editionId;
                                }
                            });
                    }
                }
            });

        if (this.creatingLesson) {
            this.initLessonBuilderContent();
        } else if (this.creatingActivity) {
            this.initActivityBuilderContent();
        }
    }

    initLessonBuilderContent() {
        this.cartStore
            .pipe(select(fromCartStore.getLessonFormData))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((data: Bundle | null) => {
                if (data) {
                    this.lessonFormData = data;
                    this.cdr.detectChanges();
                }
            });

        this.cartStore
            .pipe(select(fromCartStore.getLessonFormValidity))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((validity) => {
                this.lessonFormValidity = validity;
            });

        this.cartStore
            .pipe(select(fromCartStore.getEditingLesson))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((editingLesson) => {
                this.editingLesson = editingLesson;

                if (this.editingLesson) {
                    this.headerTitle = "Edit Bundle";
                    this.helperText =
                        "Fill out the form below to edit a Bundle and add it to your assign cart.";
                } else {
                    this.headerTitle = "Create Bundle";
                    this.helperText =
                        "Fill out the form below to create a Bundle and add it to your assign cart.";
                }
            });
    }

    onSetLessonFormData(data: Bundle) {
        this.cartStore.dispatch(
            fromCartStore.setLessonFormData({ payload: data })
        );
    }

    initActivityBuilderContent() {
        this.cartStore
            .pipe(select(fromCartStore.getCustomActivityFormData))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((data: CustomActivity | null) => {
                this.customActivityFormData = data;
                this.cdr.detectChanges();
            });

        this.cartStore
            .pipe(select(fromCartStore.getPerformActivityFormData))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe(data => {
                if (data) {
                    this.performActivityFormData = data;//Testing for this to be non-falsy, as I suspect that we don't want to reset it to null.
                    this.cdr.detectChanges();
                }
            });

        this.loginStore
            .pipe(select(fromLoginStore.getIsTeacher))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((result) => {
                if (typeof result === "boolean" && result === true) {
                    this.isTeacher = result;
                }
            });

        this.loginStore
            .pipe(select(fromLoginStore.getIsDistributor))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((result) => {
                if (typeof result === "boolean" && result === true) {
                    this.isDistributor = result;
                }
            });

        this.cartStore
            .pipe(select(fromCartStore.getActivityFormValidity))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((validity) => {
                this.activityFormValidity = validity;
            });

        this.cartStore
            .pipe(select(fromCartStore.getEditingActivity))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((editingActivity) => {
                this.editingActivity = editingActivity;
                if (this.editingActivity) {
                    this.headerTitle = "Edit Activity";
                    this.helperText =
                        "Fill out the form below to edit an activity and add it to your assign cart.";
                } else {
                    this.headerTitle = "Create Activity";
                    this.helperText =
                        "Fill out the form below to create an activity and add it to your assign cart.";
                }
            });
    }

    onSetCustomActivityFormData(data: CustomActivity) {
        this.cartStore.dispatch(
            fromCartStore.setCustomActivityFormData({ payload: data })
        );
    }

    setLessonFormValidity(validity: boolean) {
        this.cartStore.dispatch(
            fromCartStore.setLessonFormValidity({ payload: validity })
        );
    }
    setLessonFormData(lessonForm: Bundle) {
        this.cartStore.dispatch(
            fromCartStore.setLessonFormData({ payload: lessonForm })
        );
        if (this.editingLesson && this.clonedAsset) {
            this.createStore.dispatch(fromCreateStore.clearClonedAsset());
        }
    }

    setActivityFormValidity(validity: boolean) {
        this.cartStore.dispatch(
            fromCartStore.setActivityFormValidity({ payload: validity })
        );
    }
    setCustomActivityFormData(activityForm: CustomActivity) {
        this.cartStore.dispatch(
            fromCartStore.setCustomActivityFormData({ payload: activityForm })
        );
    }

    setPerformActivityFormData(activityForm: Perform) {
        this.cartStore.dispatch(
            fromCartStore.setPerformActivityFormData({ payload: activityForm })
        );
    }

    getCreateAddDisabled() {
        if (this.creatingActivity && !this.activityFormValidity) {
            return true;
        } else if (
            !this.creatingActivity &&
            this.selectedAssets?.assets.length === 0
        ) {
            return true;
        }
    }

    createActivity(create: boolean, createAdd: boolean) {
        if (!this.preventDoubleClick) {
            if (this.customActivityFormData) {
                if (this.editingActivity) {
                    this.onUpdateCustomActivity(
                        this.customActivityFormData,
                        create,
                        createAdd
                    );
                } else if (this.creatingActivity && !this.editingActivity) {
                    this.onCreateCustomActivity(
                        this.customActivityFormData,
                        create,
                        createAdd
                    );
                }
            } else if (this.performActivityFormData) {
                const performActivityFormData = {
                    ...this.performActivityFormData,
                    displayName:
                        this.performActivityFormData.displayName.trim(), // remove any potential trailing whitespace
                    audit: {
                        createdBy: this.performActivityFormData.publishedBy,
                        createdAt: new Date().toString(),
                    }, // The end point needs this or it throws a 500 error
                };
                if (this.editingActivity) {
                    this.onUpdateAvenueActivity(
                        performActivityFormData,
                        create,
                        createAdd
                    );
                } else if (this.creatingActivity && !this.editingActivity) {
                    this.onCreateAvenueActivity(
                        performActivityFormData,
                        create,
                        createAdd
                    );
                }
            }
        }
    }

    // Create and Edit Custom Activities
    onCreateCustomActivity(
        activityForm: CustomActivity,
        create: boolean,
        createAdd: boolean
    ) {
        this.preventDoubleClick = true;
        this.createStore.dispatch(
            fromCreateStore.createAsset({ payload: activityForm, createAdd })
        );

        this.cartStore.dispatch(
            fromCartStore.setCustomActivityFormData({ payload: null })
        );

        this.createStore
            .pipe(select(fromCreateStore.getSelectedAssets))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((selectedAssets: SelectedAssets) => {
                this.selectedAssets = selectedAssets;
            });

        // Add newly created assignable and add it to the cart
        if (createAdd) {
            this.handleActivityCreateAdd();
        } else {
            this.handleActivityCreate();
        }
    }

    onUpdateCustomActivity(
        activityForm: CustomActivity,
        save: boolean,
        saveAdd: boolean
    ) {
        this.preventDoubleClick = true;
        this.createStore.dispatch(
            fromCreateStore.updateAsset({ payload: activityForm })
        );

        // reload the assets table
        this.createStore
            .pipe(select(fromCreateStore.getAssetPaginationParams))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((params) => {
                this.params = params;
            });

        this.cartStore.dispatch(
            fromCartStore.setCustomActivityFormData({ payload: null })
        );

        if (saveAdd) {
            this.handleActivitySaveAdd();
        } else {
            this.handleActivitySave();
        }
    }

    // Create and Edit Avenue Activities
    onCreateAvenueActivity(
        activityForm: Perform,
        create: boolean,
        createAdd: boolean
    ) {
        this.preventDoubleClick = true;
        // need to map 6 to undefined which is unlimited, 6 is a placeholder value for the selector
        activityForm = {
            ...activityForm,
            recordingTries:
                activityForm.recordingTries === 6
                    ? -1
                    : activityForm.recordingTries,
            views:
                activityForm.views === 6
                    ? -1
                    : activityForm.recordingTries || 1,
        };

        this.createStore.dispatch(
            fromCreateStore.createAsset({ payload: activityForm, createAdd })
        );

        this.cartStore.dispatch(
            fromCartStore.setPerformActivityFormData({ payload: null })
        );

        // Add newly created assignable and add it to the cart
        if (createAdd) {
            this.handleActivityCreateAdd();
        } else {
            this.handleActivityCreate();
        }
    }
    onUpdateAvenueActivity(
        activityForm: Perform,
        save: boolean,
        saveAdd: boolean
    ) {
        this.preventDoubleClick = true;
        // need to map 6 to undefined which is unlimited, 6 is a placeholder value for the selector
        // TODO: CURE: Pass teacher-class maxAttempts value here
        // activityForm = {
        //     ...activityForm,
        //     recordingTries:
        //         activityForm.recordingTries === 6
        //             ? undefined
        //             : activityForm.recordingTries
        // };
        this.createStore.dispatch(
            fromCreateStore.updateAsset({ payload: activityForm })
        );

        // reload the assignables table
        this.createStore
            .pipe(select(fromCreateStore.getAssetPaginationParams))
            .pipe(takeUntil(this.ngUnsubscribe))
            .subscribe((params) => {
                this.params = params;
            });

        this.cartStore.dispatch(
            fromCartStore.setPerformActivityFormData({ payload: null })
        );

        if (saveAdd) {
            this.handleActivitySaveAdd();
        } else {
            this.handleActivitySave();
        }
    }

    handleActivityCreateAdd() {
        this.cartStore.dispatch(
            fromCartStore.setCreatingActivity({ payload: false })
        );
        this.cartStore.dispatch(
            fromCartStore.setActivityFormValidity({
                payload: false,
            })
        );

        this.closeBuilder();
    }
    handleActivitySaveAdd() {
        this.cartStore.dispatch(
            fromCartStore.setContainsComposite({ payload: false })
        );
        this.cartStore.dispatch(
            fromCartStore.setCreatingActivity({ payload: false })
        );
        this.cartStore.dispatch(
            fromCartStore.setEditingActivity({ payload: false })
        );
        this.cartStore.dispatch(
            fromCartStore.setActivityFormValidity({ payload: false })
        );

        this.actions$
            .pipe(
                ofType(assetsActions.updateAssetSuccess),
                takeUntil(this.ngUnsubscribe)
            )
            .subscribe((asset) => {
                if (!this.selectedAssets) {
                    this.selectedAssets = { editionId: "", assets: [] };
                }
                let selectedAssets = this.selectedAssets.assets.slice();

                // If you add an asset to the assign cart, then edit it, and attempt to add it again.
                // We need to clear the cart of the first, now outdated, asset.
                const assetExistsInArray = this.selectedAssets.assets.find(
                    (a) => a.assetId === asset.payload.assetId
                );
                if (assetExistsInArray) {
                    const cleanArray: Asset[] = [];
                    selectedAssets.forEach((a) => {
                        if (a.assetId !== asset.payload.assetId) {
                            cleanArray.push(a);
                        }
                    });
                    selectedAssets = cleanArray;
                }

                selectedAssets.push(asset.payload);
                this.selectedAssets = {
                    ...this.selectedAssets,
                    assets: selectedAssets,
                };
                this.createStore.dispatch(
                    fromCreateStore.SetSelectedAssets({
                        payload: this.selectedAssets,
                    })
                );
                this.closeBuilder();
            });
    }

    handleActivityCreate() {
        // Go back to Content Library
        if (this.selectedAssets && this.selectedAssets.assets.length > 0) {
            this.cartStore.dispatch(
                fromCartStore.setAssignCartTabOpen({ payload: true })
            );
        }
        this.sidePanelRightClose.emit(); // keep this before setting other properties
        this.cartStore.dispatch(
            fromCartStore.setAssignCartOpen({ payload: false })
        );
        this.cartStore.dispatch(
            fromCartStore.setCreatingActivity({ payload: false })
        );
        this.cartStore.dispatch(
            fromCartStore.setActivityFormValidity({
                payload: false,
            })
        );
        this.closeBuilder();
    }
    handleActivitySave() {
        if (
            !this.selectedAssets ||
            (this.selectedAssets && this.selectedAssets.assets.length === 0)
        ) {
            this.sidePanelRightClose.emit(); // keep this before setting other properties
            this.cartStore.dispatch(
                fromCartStore.setAssignCartOpen({ payload: false })
            );
        }

        this.cartStore.dispatch(
            fromCartStore.setCreatingActivity({ payload: false })
        );
        this.cartStore.dispatch(
            fromCartStore.setEditingActivity({ payload: false })
        );
        this.cartStore.dispatch(
            fromCartStore.setActivityFormValidity({ payload: false })
        );
        this.closeBuilder();
    }

    routeToActivitiesTab() {
        this.router.navigate([`teacher/assign/${this.subjectId}`]);
    }

    clearActivityForm() {
        this.cartStore.dispatch(
            fromCartStore.setCustomActivityFormData({ payload: null })
        );

        this.cartStore.dispatch(
            fromCartStore.setPerformActivityFormData({ payload: null })
        );
    }

    closeBuilder() {
        if (this.creatingLesson) {
            /* this.createStore.dispatch(
                new fromCreateStore.SetSelectedAssignables([])
            ); */
            this.createStore.dispatch(fromCreateStore.clearLesson());
            // this.localStorage.remove("selectedAssignables");
            this.cartStore.dispatch(
                fromCartStore.setCreatingLesson({ payload: false })
            );
            this.cartStore.dispatch(
                fromCartStore.setEditingLesson({ payload: false })
            );
            this.cartStore.dispatch(
                fromCartStore.setLessonFormData({ payload: null })
            );
            this.cartStore.dispatch(
                fromCartStore.setLessonFormValidity({ payload: false })
            );
            if (this.editingLesson) {
                this.selectedAssets = { ...this.selectedAssets, assets: [] };
                this.createStore.dispatch(
                    fromCreateStore.SetSelectedAssets({
                        payload: this.selectedAssets,
                    })
                );
            }
            this.createStore.dispatch(fromCreateStore.clearClonedAsset());
        } else if (this.creatingActivity) {
            this.cartStore.dispatch(
                fromCartStore.setCreatingActivity({ payload: false })
            );
            this.cartStore.dispatch(
                fromCartStore.setEditingActivity({ payload: false })
            );
            this.cartStore.dispatch(
                fromCartStore.setCustomActivityFormData({ payload: null })
            );
            this.cartStore.dispatch(
                fromCartStore.setPerformActivityFormData({ payload: null })
            );
            this.cartStore.dispatch(
                fromCartStore.setActivityFormValidity({ payload: false })
            );
            this.createStore.dispatch(fromCreateStore.clearClonedAsset());
        }
    }

    ngOnDestroy() {
        this.ngUnsubscribe.next(true);
        this.ngUnsubscribe.complete();
    }
}
